import React, { useState } from 'react';
import { X, Send, Mail, Trash2, Snowflake } from 'lucide-react';
import Cal from "@calcom/embed-react";
import { companyInfo, services } from '../data/mock';

const EstimateModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    specificService: '',
    address: '',
    message: '',
  });

  const handleClose = () => {
    // 1. Reseteamos la vista para que la próxima vez inicie en el formulario
    setShowScheduler(false);
    // 2. Opcional: Si quieres que el formulario se limpie al cerrar, descomenta esto:
    // setFormData(initialFormState); 
    // setErrors({});
    
    // 3. Cerramos el modal
    onClose();
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [errors, setErrors] = useState({});

  const serviceTypes = [
    { id: 'junk', label: 'Junk Removal', icon: Trash2 },
    { id: 'snow', label: 'Snow Removal', icon: Snowflake },
    { id: 'both', label: 'Both Services', icon: null },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const getSelectedServices = () => {
   
    const selectedServiceLabel = formData.serviceType === 'junk' ? 'Junk removal' : 
    formData.serviceType === 'snow' ? 'Snow removal' : 
    'Both services'; // O como lo hayas nombrado en el Select
    return selectedServiceLabel;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.serviceType) newErrors.serviceType = 'Please select a service';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    setIsSubmitting(false);
    setShowScheduler(true);
  };

  if (!isOpen) return null;

  // Preparamos los parámetros igual que en tu código que funciona
  const params = new URLSearchParams({
    name: formData.name,
    email: formData.email,
    service: getSelectedServices(),
    specific: formData.specificService || "General Inquiry",
    location: formData.address,
    address: formData.address,
    notes: formData.message,
    theme: "dark"
  }).toString();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      <div className={`relative bg-[#141414] border border-[#2A2A2A] rounded-3xl w-full transition-all duration-500 shadow-2xl overflow-hidden flex flex-col ${showScheduler ? 'max-w-4xl h-[85vh]' : 'max-w-2xl'
        }`}>

        {/* Botón Cerrar */}
        <button onClick={onClose} className="absolute top-4 right-4 z-50 w-10 h-10 bg-[#1A1A1A] rounded-full flex items-center justify-center hover:bg-[#2A2A2A] transition-colors">
          <X className="w-5 h-5 text-gray-400" />
        </button>


        <div className={`${showScheduler ? 'p-0 h-full' : 'p-8 md:p-12'}`}>
          {showScheduler ? (
            <div className="animate-in fade-in zoom-in duration-300 h-full flex flex-col">
              <div className="p-6 pb-0 text-center shrink-0">
                <h3 className="text-2xl font-bold text-white mb-2">Book Your Visit</h3>
                <p className="text-gray-400">Select a time for your estimate</p>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar">
              <Cal
                calLink={`edson-cardona-tmhica/junkandsnow?${params}`}
                style={{ width: "100%", height: "100%" }}
                config={{ layout: "month_view", theme: "dark" }}
              />
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8 shrink-0">
                <h3 className="text-2xl font-bold text-white mb-2">Get Your Free Estimate</h3>
                <p className="text-gray-400">Fill out your details to schedule a visit.</p>
              </div>

              {/* Contenedor con scroll para el formulario si el contenido excede el alto del modal */}
              <form
                onSubmit={handleSubmit}
                className="space-y-6 overflow-y-auto pr-2 max-h-[60vh] scrollbar-thin scrollbar-thumb-[#2A2A2A]"
              >
                {/* Service Type */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-white">What service do you need? *</label>
                  <div className="grid grid-cols-3 gap-3">
                    {serviceTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, serviceType: type.id, specificService: '' }))}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center justify-center min-h-[100px] ${formData.serviceType === type.id
                            ? 'border-[#7BA428] bg-[#7BA428] text-black'
                            : 'border-[#2A2A2A] bg-[#1A1A1A] text-gray-300 hover:border-[#7BA428]/50'
                          }`}
                      >
                        {type.icon && <type.icon className="w-6 h-6 mb-2" />}
                        <span className="text-sm font-medium leading-tight">{type.label}</span>
                      </button>
                    ))}
                  </div>
                  {errors.serviceType && (
                    <p className="text-red-500 text-sm">{errors.serviceType}</p>
                  )}
                </div>

                {/* Specific Service Dropdown */}
                {formData.serviceType && formData.serviceType !== 'both' && (
                  <div className="animate-in slide-in-from-top-2 duration-300">
                    <label className="block text-sm font-semibold text-white mb-2">
                      Specific Service (Optional)
                    </label>
                    <select
                      name="specificService"
                      value={formData.specificService}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-[#2A2A2A] bg-[#1A1A1A] text-white focus:border-[#7BA428] focus:outline-none transition-colors"
                    >
                      <option value="">Select a specific service...</option>
                      {services
                        .filter(s => s.category === formData.serviceType)
                        .map(service => (
                          <option key={service.id} value={service.title}>
                            {service.title}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Name & Phone */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className={`w-full px-4 py-3 rounded-xl border-2 bg-[#1A1A1A] text-white placeholder:text-gray-500 focus:outline-none transition-colors ${errors.name ? 'border-red-500' : 'border-[#2A2A2A] focus:border-[#7BA428]'
                        }`}
                    />
                    {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-white">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(780) 555-0123"
                      className={`w-full px-4 py-3 rounded-xl border-2 bg-[#1A1A1A] text-white placeholder:text-gray-500 focus:outline-none transition-colors ${errors.phone ? 'border-red-500' : 'border-[#2A2A2A] focus:border-[#7BA428]'
                        }`}
                    />
                    {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-white">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-xl border-2 border-[#2A2A2A] bg-[#1A1A1A] text-white placeholder:text-gray-500 focus:border-[#7BA428] focus:outline-none transition-colors"
                  />
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-white">Service Address *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main St, Edmonton, AB"
                    className={`w-full px-4 py-3 rounded-xl border-2 bg-[#1A1A1A] text-white placeholder:text-gray-500 focus:outline-none transition-colors ${errors.address ? 'border-red-500' : 'border-[#2A2A2A] focus:border-[#7BA428]'
                      }`}
                  />
                  {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}
                </div>


                <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Additional Details
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about your project (items to remove, size of driveway, etc.)"
                  className="w-full px-4 py-3 rounded-xl border-2 border-[#2A2A2A] bg-[#1A1A1A] text-white placeholder:text-gray-500 focus:border-[#7BA428] focus:outline-none transition-colors resize-none"
                />
              </div>

                {/* Submit Button - Sticky al final del scroll */}
                <div className="pt-2 sticky bottom-0 bg-[#141414]">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#7BA428] text-black py-4 rounded-full font-bold text-lg hover:bg-[#B8E550] transition-all flex items-center justify-center gap-2 shadow-xl"
                  >
                    {isSubmitting ? "Processing..." : "Next: Schedule Now"}
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EstimateModal;